import type { SerialPortInfo } from './types/SharedTypes';

export type { SerialPortInfo };

/**
 * Approved barcode scanner hardware: USB vendor ID 24EA, plus the legacy
 * product-ID allowlist for older scanner models that report a different
 * vendor. Anything else on a COM port must never be opened as a scanner.
 */
export const BARCODE_SCANNER_VENDOR_ID = '24EA';
export const BARCODE_PID_FILTERS = ['187', '104', '7523'];

export function normalizePid(value?: string | null): string {
  return String(value ?? '').replace(/^0+/, '').trim();
}

export function isBarcodeScannerPort(port: SerialPortInfo): boolean {
  const vendorId = String(port.vendorId ?? '').trim().toUpperCase();
  if (vendorId === BARCODE_SCANNER_VENDOR_ID) return true;
  // Windows exposes the vendor via the PnP ID ("USB\VID_24EA&PID_...") even
  // when the vendorId field is missing.
  const pnpId = String(port.pnpId ?? '').toUpperCase();
  if (pnpId.includes(`USB\\VID_${BARCODE_SCANNER_VENDOR_ID}`)) return true;
  return BARCODE_PID_FILTERS.includes(normalizePid(port.productId));
}

/** Strip control chars (STX/ETX etc.) that some USB scanners prefix/suffix. */
export function normalizeScannerPayload(data: string): string {
  return String(data ?? '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/[\r\n]+/g, '')
    .trim();
}

/**
 * Split accumulated serial chunks into complete scan lines (terminated by CR/LF).
 * Returns any trailing partial line to keep in the buffer.
 */
export function consumeScannerStreamBuffer(
  buffer: string,
  chunk: string,
): { nextBuffer: string; lines: string[] } {
  const combined = buffer + chunk;
  const parts = combined.split(/\r\n|\n|\r/);
  const nextBuffer = parts.pop() ?? '';
  const lines: string[] = [];
  for (const part of parts) {
    const line = normalizeScannerPayload(part);
    if (line) lines.push(line);
  }
  return { nextBuffer, lines };
}

export async function listSerialPortsSafe(): Promise<SerialPortInfo[]> {
  try {
    const res = await window.serialAPI?.listSerialPorts?.();
    return Array.isArray(res?.message) ? (res.message as SerialPortInfo[]) : [];
  } catch {
    return [];
  }
}

export function findBarcodePort(ports: SerialPortInfo[]): SerialPortInfo | undefined {
  return ports.find(isBarcodeScannerPort);
}

export async function getBarcodeScannerPort(): Promise<SerialPortInfo | null> {
  const ports = await listSerialPortsSafe();
  return findBarcodePort(ports) ?? null;
}

export function getScannerStatusText(
  port: SerialPortInfo | null | undefined,
  active: boolean,
  noScannerText = 'Waiting for scanner connection',
): string {
  if (!port) return noScannerText;
  if (!active) return 'Scanner found, connecting...';
  return 'Scanner connected';
}

export async function openSerialPortSafe(
  path?: string | null,
  baudRate = 9600,
): Promise<{ success: boolean; message?: string }> {
  if (!path) return { success: false, message: 'Missing serial port path' };
  try {
    const res = await window.serialAPI?.openSerialPort?.(path, baudRate);
    return {
      success: res?.success === true,
      message: res?.message ? String(res.message) : undefined,
    };
  } catch (err: unknown) {
    return {
      success: false,
      message: (err as Error)?.message || 'Failed to open scanner',
    };
  }
}

export async function closeSerialPortSafe(): Promise<{ success: boolean; message?: string }> {
  try {
    const res = await window.serialAPI?.closeSerialPort?.();
    return {
      success: res?.success === true,
      message: res?.message ? String(res.message) : undefined,
    };
  } catch (err: unknown) {
    return {
      success: false,
      message: (err as Error)?.message || 'Failed to close scanner',
    };
  }
}

export function onSerialDataSafe(
  handler: (event: unknown, data: string) => void,
): () => void {
  if (!window.serialAPI?.onSerialData) {
    return () => {};
  }
  const unsubscribe = window.serialAPI.onSerialData(handler);
  return typeof unsubscribe === 'function' ? unsubscribe : () => {};
}
